import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/layout/PageLayout';
import { Card, IndicatorCard } from '@/components/ui/cards';
import type { IndicatorCardVariant } from '@/components/ui/cards';
import { Skeleton } from '@/components/ui/loading';
import { MonitoringFiltersPanel } from '@/components/features/attendance/monitoring/MonitoringFiltersPanel';
import type { LC_MonitoringFiltersIn, MonitoringCounters } from '@/api/domains/attendance/monitoring';
import { useMonitoringCounters } from '@/hooks/attendance/monitoring/useMonitoringCounters';


const COUNTER_CARDS: { key: keyof MonitoringCounters; variant: IndicatorCardVariant }[] = [
  { key: 'totalActive', variant: 'important' },
  { key: 'absent', variant: 'error' },
  { key: 'justified', variant: 'alert' },
  { key: 'present', variant: 'success' },
];

export const AttendanceMonitoringPage: React.FC = () => {
  const { t: translate } = useTranslation();

  // Last payload emitted by the filters panel (live, no "Consultar" button); null until first emit.
  const [appliedFilters, setAppliedFilters] = useState<LC_MonitoringFiltersIn | null>(null);

  const handleApply = useCallback((payload: LC_MonitoringFiltersIn) => {
    setAppliedFilters(payload);
  }, []);

  const { counters, loading } = useMonitoringCounters(appliedFilters);

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="attendance-monitoring-page">
        <div
          className="grid grid-cols-2 gap-5 lg:grid-cols-4"
          data-testid="monitoring-counters"
        >
          {COUNTER_CARDS.map(({ key, variant }) => (
            loading ? (
              <Skeleton
                key={key}
                height="104px"
                data-testid={`monitoring-counter-skeleton-${key}`}
              />
            ) : (
              <IndicatorCard
                key={key}
                label={translate(`attendance:monitoring.counters.${key}`)}
                value={counters ? counters[key] : 0}
                variant={variant}
                padValue={false}
                data-testid={`monitoring-counter-${key}`}
              />
            )
          ))}
        </div>

        <MonitoringFiltersPanel onApply={handleApply} />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {(['absent', 'justified', 'present'] as const).map((key) => (
            <Card key={key}>
              <div
                className="flex flex-col gap-3 p-4"
                data-testid={`monitoring-list-placeholder-${key}`}
              >
                <h3 className="text-sm font-semibold text-gray-700">
                  {translate(`attendance:monitoring.placeholders.${key}`)}
                </h3>
                <div className="flex h-40 items-center justify-center rounded-md border border-dashed border-gray-300 text-sm text-gray-400">
                  {translate('attendance:monitoring.placeholders.listsPending')}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </PageLayout>
  );
};

export default AttendanceMonitoringPage;
